import { Hash, MessageCircle, Text } from "@tamagui/lucide-icons";
import type React from "react";
import { useMemo } from "react";
import {
  FormDataModal,
  type FormDataModalProps,
  FormSheetType,
  type FormValues,
} from "../../../core/components/FormDataModal";
import type { TrackerHistoryItem } from "../data/TrackerUserData";
import type TrackerModuleModel from "../TrackerModuleModel";

export const AddEntryModal: React.FC<
  Omit<FormDataModalProps, "values" | "onSave" | "title" | "form"> & {
    entry?: TrackerHistoryItem;
    module: TrackerModuleModel;
  }
> = (props) => {
  return (
    <FormDataModal
      {...props}
      title="Add Entry"
      values={useMemo(
        () =>
          props.entry ?? {
            type: "",
            amount: "",
            comment: "",
          },
        [props.entry],
      )}
      form={[
        {
          required: true,
          formKey: "type",
          placeholder: "Type",
          type: FormSheetType.SelectInline,
          icon: Text,
          options: props.module.selectableOptions.map((o) => ({
            label: o.label + (!o.isDynamic ? ` (${o.weight}p)` : ""),
            value: o.uid,
          })),
        },
        {
          formKey: "amount",
          placeholder: "Amount",
          inputMode: "numeric",
          type: FormSheetType.Input,
          icon: Hash,
          isActiveBasedOnData: (formData: FormValues) => {
            if (!formData.type) {
              return false;
            }

            const option = props.module.getOption(formData.type);
            return option?.isDynamic ?? false;
          },
          required: true,

          validator: (v: unknown) => {
            if (typeof v === "string") {
              const parsedValue = Number.parseFloat(v);
              if (!Number.isNaN(parsedValue) && `${parsedValue}` === v) {
                // v is a valid number
                return true;
              }
            }
            // v is not a valid number
            return false;
          },
        },
        {
          formKey: "comment",
          placeholder: "Comment",
          type: FormSheetType.Input,
          icon: MessageCircle,
        },
      ]}
      onSave={(data) => {
        if (!props.module.plan.userInPlan) {
          return;
        }

        props.module.addEntry({
          ts: props.entry?.ts ?? undefined,
          comment: data.comment,
          amount: data.amount ? Number.parseFloat(data.amount) : 1,
          type: data.type,
        });
      }}
    />
  );
};
